/**
 * Mob base: vanilla-style living entity physics (gravity 0.08, drag 0.98,
 * ground friction, step-up 0.6, water/lava, fall damage, fire) and the shared
 * AI helpers (stroll, look-at-player, safe-ahead checks). Concrete mobs live in
 * hostile.ts / animals.ts and only fill in `ai()`, loot and a few traits.
 *
 * Speeds: `speed` is the vanilla movement attribute and is used as the
 * terminal ground speed in blocks/tick (zombie 0.23, creeper 0.25, ...).
 */

import {
  collisionOf, IS_LAVA, FIRE_ID, FULL_CELL_BOXES, IS_WATERY, MAGMA_ID, NO_BOXES,
} from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { clipMove, type ShapesAt } from '../world/collide';
import type { World } from '../world/chunk';
import type { MobType, MobView, Stack } from '../game/types';
import { armorPoints } from './overworld2/variants';
import { speedMult, tickMobEffects, type EffectSet } from '../game/effects/effects';
import { approachAngle, wrapAngle, yawTo, type EntityHost } from './host';
import { realm } from './realm';
import { Navigator, newSteer, type NavEnv } from './nav/navigator';
import { HOT_FLOOR, K_BLOCKED, K_DOOR_OPEN, K_LAVA, K_OPEN, KIND, TOP } from './nav/nodes';

const EPS = 1e-5;
const GRAVITY = 0.08;
const JUMP_VELOCITY = 0.42;
/** Max head turn away from the body before the body follows (vanilla 75 degrees). */
const MAX_HEAD_TURN = 1.309;
const BODY_TURN = 0.6;
const HEAD_TURN = 0.5;
const ATTACK_SWING_TICKS = 6;
/** Ground friction: slipperiness 0.6 * 0.91. */
const GROUND_DRAG = 0.546;
const NO_Y = -1e9;

/** Block edits the navigator may make, routed to whichever host the mob runs in. */
let navHost: EntityHost | null = null;
const NAV_ENV: NavEnv = {
  setBlock: (x, y, z, id) => navHost?.setBlock?.(x, y, z, id),
  crack: (x, y, z, stage) => navHost?.crackDoor?.(x, y, z, stage),
};

export interface MobSpec {
  width: number; height: number; eye: number;
  health: number; speed: number;
}

/** Services the manager gives a mob each tick. */
export interface MobCtx {
  readonly host: EntityHost;
  readonly world: World;
  readonly tick: number;
  /** Is the straight line between two points free of opaque blocks? */
  los(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean;
  /** Player can be targeted (alive, survival, not spectator). */
  targetable(): boolean;
  /** Melee hit on the player (damage + knockback). */
  hurtPlayer(mob: Mob, amount: number, knockback: number): void;
  shootArrow(x: number, y: number, z: number, dx: number, dy: number, dz: number,
    speed: number, damage: number, fromPlayer: boolean, inaccuracy: number, shooterId: number): void;
  explodeCreeper(mob: Mob): void;
  /** Drowned-style thrown trident (8 damage, drawn as a trident). */
  shootTrident?(x: number, y: number, z: number, dx: number, dy: number, dz: number, speed: number, inaccuracy: number, shooterId: number): void;
}

export abstract class Mob {
  readonly id: number;
  readonly type: MobType;
  readonly width: number;
  readonly height: number;
  readonly eyeHeight: number;
  readonly speed: number;
  readonly maxHealth: number;
  health: number;

  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx = 0; vy = 0; vz = 0;
  yaw = 0; headYaw = 0; pitch = 0;

  onGround = false;
  inWater = false;
  inLava = false;
  collidedH = false;
  hurtTime = 0;
  /** Invulnerability ticks left (vanilla 20 after a hit). */
  hurtResist = 0;
  lastHurt = 0;
  deathTime = 0;
  dying = false;
  fireTicks = 0;
  fallDistance = 0;
  limbSwing = 0;
  limbSwingAmount = 0;
  swingTicks = 0;
  swell = 0;
  aggressive = false;
  sheared = false;
  color = 0;
  heldItem = 0;
  /** The host's (seeded) rng: use instead of Math.random so tests stay deterministic. */
  protected readonly rng: () => number;
  /** Worn armour item ids helmet..boots (zombie/skeleton variants); empty = none. */
  armorPieces: number[] = [];
  /** Extra melee damage from a held weapon. */
  meleeBonus = 0;
  /** Half-size undead baby (animals track theirs themselves). */
  baby = false;
  /** Name tag name (shown above the mob; saved). */
  customName = '';
  /** Player id of who tamed this mob ('' = nobody, or an offline world where there is one player). */
  owner = '';
  /** The world this mob lives in (entities/realm.ts): registries shared by every world skip the others. */
  readonly realm = realm.id;
  /** Never despawns (a name tag was used on it). */
  persistent = false;
  age = 0;
  /** Ticks since the player last hurt it (> 0: kills give XP). */
  lastHurtByPlayer = 0;
  /** Id of the player that last hurt it ('' = the local player / unknown); valid while lastHurtByPlayer > 0. */
  lastAttacker = '';
  /** Ticks left of grudge: ignores line-of-sight / daylight neutrality. */
  retaliate = 0;
  removed = false;
  /** Active status effects (game/effects), created on the first one. */
  effects: EffectSet | null = null;

  /** Animals flee for this many ticks after being hurt. */
  panicTicks = 0;
  protected panicAngle = 0;

  // movement intent for this tick (set by ai())
  protected moveDX = 0;
  protected moveDZ = 0;
  protected moveMod = 0;
  protected wantJump = false;
  private faceX = 0; private faceZ = 0; private faceSet = false;
  private lookX = 0; private lookY = 0; private lookZ = 0; private lookSet = false;
  protected climbs = false;
  protected noFallDamage = false;
  protected slowFall = false;
  // stroll state: 0 idle, 1 walking to a picked spot, 2 legacy heading (no spot found)
  private idleTicks: number;
  private strollTicks = 0;
  private strollMode = 0;
  private strollDX = 0;
  private strollDZ = 0;
  private strollX = 0; private strollY = 0; private strollZ = 0;
  // navigation
  private navigator: Navigator | null = null;
  private readonly steer = newSteer();
  /** Spot chosen by pickTarget. */
  private tgtX = 0; private tgtY = 0; private tgtZ = 0;
  private fleeX = 0; private fleeY = NO_Y; private fleeZ = 0; private fleeUntil = 0;
  private shadeX = 0; private shadeY = NO_Y; private shadeZ = 0; private shadeUntil = 0;
  /** Water mobs: move through water cells only. */
  protected swimmer = false;
  /** Striders: lava counts as a floor. */
  protected lavaWalker = false;
  /** Max ledge climbed while walking (0.6 vanilla; horses 1.0). */
  stepHeight = 0.6;
  /** Leashed (entities/leash.ts): stroll targets stay within `r` of (x, z). */
  leashHome: { x: number; z: number; r: number } | null = null;
  /** Wears a saddle (pig / strider / horse; entities/riding). */
  saddled = false;
  /** Rider control (game/mounts.ts): while set it replaces `ai()`. */
  rideCtl: ((ctx: MobCtx) => boolean) | null = null;
  /** Rider's eyes under water (a ridden mount dismounts, vanilla). */
  get eyeInWater(): boolean { return !!this.w && IS_WATERY[this.w.getBlock(Math.floor(this.x), Math.floor(this.eyeY + 0.5), Math.floor(this.z))] === 1; }
  /** Steer this tick (rider control): unit direction, speed modifier, jump. */
  setRideMove(dx: number, dz: number, mod: number, jump: boolean): void {
    this.setMove(dx, dz, mod);
    if (jump) this.wantJump = true;
  }
  /** Face along `yaw` at once (ridden mounts follow their rider). */
  setRideYaw(yaw: number): void { this.yaw = this.headYaw = yaw; }
  private lookTicks = 0;

  protected w!: World;
  protected host!: EntityHost;
  /** The services of the tick in progress (for helpers that are not handed `ctx`). */
  protected ctx!: MobCtx;
  readonly view: MobView;
  private readonly box = [0, 0, 0, 0, 0, 0];

  constructor(id: number, type: MobType, x: number, y: number, z: number, spec: MobSpec, rnd: () => number) {
    this.id = id;
    this.rng = rnd;
    this.type = type;
    this.width = spec.width;
    this.height = spec.height;
    this.eyeHeight = spec.eye;
    // The attribute values are vanilla's, but used 1:1 as blocks/tick they ran mobs at
    // sprint speed and above; scale them to the pace they actually walk at.
    const animal = type === 'pig' || type === 'cow' || type === 'sheep' || type === 'chicken';
    this.speed = spec.speed * (animal ? 0.4 : 0.55);
    this.maxHealth = spec.health;
    this.health = spec.health;
    this.x = this.px = x;
    this.y = this.py = y;
    this.z = this.pz = z;
    this.yaw = this.headYaw = rnd() * Math.PI * 2 - Math.PI;
    this.idleTicks = 20 + Math.floor(rnd() * 100);
    this.view = {
      id, type, x, y, z, px: x, py: y, pz: z, yaw: this.yaw, headYaw: this.headYaw, pitch: 0,
      limbSwing: 0, limbSwingAmount: 0, hurtTime: 0, deathTime: 0, swell: 0, attackAnim: 0,
      onFire: false, heldItem: 0, sheared: false, color: 0, aggressive: false, light: 1,
    };
  }

  get alive(): boolean { return !this.dying && !this.removed; }
  get eyeY(): number { return this.y + this.eyeHeight; }

  // ------------------------------------------------------------------ hooks

  protected abstract ai(ctx: MobCtx): void;
  /** Drops rolled at death (`byPlayer` = hurt by the player recently). */
  abstract loot(rnd: () => number, byPlayer: boolean): Stack[];
  abstract xpValue(rnd: () => number): number;
  /** Reaction to being hurt (animals panic, hostiles retaliate). */
  onHurtBy(_fromX: number, _fromZ: number, _rnd: () => number): void {}

  // ------------------------------------------------------------------- tick

  tick(ctx: MobCtx): void {
    this.w = ctx.world;
    this.host = ctx.host;
    this.ctx = ctx;
    this.px = this.x; this.py = this.y; this.pz = this.z;
    this.age++;
    if (this.hurtTime > 0) this.hurtTime--;
    if (this.hurtResist > 0) this.hurtResist--;
    if (this.lastHurtByPlayer > 0) this.lastHurtByPlayer--;
    if (this.retaliate > 0) this.retaliate--;
    if (this.swingTicks > 0 && ++this.swingTicks > ATTACK_SWING_TICKS) this.swingTicks = 0;

    if (this.dying) {
      this.deathTime++;
      this.moveMod = 0; this.wantJump = false;
      this.travel();
      if (this.deathTime >= 20) this.removed = true;
      this.updateLimbs();
      return;
    }

    if (this.effects) tickMobEffects(this);
    if (this.dying) return;
    this.moveMod = 0;
    this.wantJump = false;
    this.faceSet = false;
    this.lookSet = false;
    this.aggressive = false;
    if (this.rideCtl && this.rideCtl(ctx)) { /* rider steers */ } else { this.ai(ctx); this.updateRotation(); }
    this.travel();
    this.postTravel(ctx);
    this.updateLimbs();
  }

  // --------------------------------------------------------------- damage

  /** Vanilla invulnerability rules. Returns whether any damage was dealt. */
  hurt(amount: number): boolean {
    if (!this.alive || amount <= 0) return false;
    if (this.armorPieces.length) amount *= 1 - Math.min(20, armorPoints(this.armorPieces)) / 25;
    let dealt = amount;
    if (this.hurtResist > 10) {
      if (amount <= this.lastHurt) return false;
      dealt = amount - this.lastHurt;
      this.lastHurt = amount;
    } else {
      this.lastHurt = amount;
      this.hurtResist = 20;
    }
    this.hurtTime = 10;
    this.health -= dealt;
    if (this.health <= 0) {
      this.health = 0;
      this.dying = true;
      this.deathTime = 0;
      this.swell = 0;
      this.aggressive = false;
    }
    return true;
  }

  /** Knockback resistance attribute 0..1 (iron golem 1, ravager 0.75, warden 1, netherite armor 0.1 per piece). */
  knockbackResist = 0;

  /**
   * Vanilla LivingEntity.knockback. (dx, dz) points from the attacker toward this mob.
   * Strength is scaled by (1 - resistance); `onGround` is left alone (vanilla: the next
   * travel still uses ground friction, and the move itself clears it).
   */
  knockback(dx: number, dz: number, strength: number, rnd: () => number): void {
    strength *= 1 - Math.min(1, Math.max(0, this.knockbackResist));
    if (strength <= 0) return;
    let len = Math.hypot(dx, dz);
    if (len < 1e-4) {
      const a = rnd() * Math.PI * 2;
      dx = Math.cos(a); dz = Math.sin(a); len = 1;
    }
    this.vx = this.vx / 2 + (dx / len) * strength;
    this.vz = this.vz / 2 + (dz / len) * strength;
    if (this.onGround) this.vy = Math.min(0.4, this.vy / 2 + strength);
  }

  /** Sprint / Knockback-enchant extra push: 0.5 per level along the attacker's yaw, +0.1 y per level. */
  knockbackExtra(levels: number, yaw: number): void {
    const k = 0.5 * levels * (1 - Math.min(1, Math.max(0, this.knockbackResist)));
    if (k <= 0) return;
    this.vx += -Math.sin(yaw) * k;
    this.vz += -Math.cos(yaw) * k;
    this.vy += 0.1 * levels;
  }

  swing(): void { if (this.swingTicks === 0) this.swingTicks = 1; }

  // --------------------------------------------------------- AI helpers

  protected setMove(dx: number, dz: number, mod: number): void {
    const l = Math.hypot(dx, dz);
    if (l < 1e-6) { this.moveMod = 0; return; }
    this.moveDX = dx / l; this.moveDZ = dz / l; this.moveMod = mod;
  }
  protected faceToward(tx: number, tz: number): void { this.faceX = tx; this.faceZ = tz; this.faceSet = true; }
  protected lookAt(tx: number, ty: number, tz: number): void { this.lookX = tx; this.lookY = ty; this.lookZ = tz; this.lookSet = true; }

  protected dist2To(tx: number, ty: number, tz: number): number {
    const dx = tx - this.x, dy = ty - (this.y + this.height / 2), dz = tz - this.z;
    return dx * dx + dy * dy + dz * dz;
  }

  /** Cheap per-mob phase so work (line-of-sight, etc.) is spread across ticks. */
  protected phase(ctx: MobCtx, every: number): boolean {
    return (ctx.tick + this.id) % every === 0;
  }

  /**
   * Is the ground ahead (0.9 blocks along dx,dz) safe to step on? Never lava,
   * fire or magma; optionally never water, and never a drop over `maxDrop`.
   */
  protected safeAhead(dx: number, dz: number, maxDrop: number, avoidWater: boolean): boolean {
    const l = Math.hypot(dx, dz);
    if (l < 1e-6) return true;
    const ax = Math.floor(this.x + (dx / l) * 0.9);
    const az = Math.floor(this.z + (dz / l) * 0.9);
    const fy = Math.floor(this.y + 0.01);
    const w = this.w;
    const feet = w.getBlock(ax, fy, az);
    if (IS_LAVA[feet] === 1 || feet === FIRE_ID || (avoidWater && IS_WATERY[feet] === 1)) return false;
    for (let k = 1; k <= maxDrop + 1; k++) {
      const id = w.getBlock(ax, fy - k, az);
      if (IS_LAVA[id] === 1 || id === FIRE_ID || id === MAGMA_ID) return false;
      if (IS_WATERY[id] === 1) return !avoidWater;
      if (collisionOf(id).length > 0) return true;
    }
    return false;
  }

  // ----------------------------------------------------------- navigation

  /** Doors this mob can deal with: 0 none, 1 open and close, 2 break (zombies on hard). */
  protected doorMode(_ctx: MobCtx): 0 | 1 | 2 { return 0; }
  /** Stroll preference of a standing spot (vanilla getWalkTargetValue): higher wins. */
  protected walkValue(_x: number, _y: number, _z: number): number { return 0; }

  /**
   * Walk toward (tx, ty, tz) along a path (or straight when the way is clear)
   * at speed modifier `mod`. False: no route, the caller steers the old way.
   */
  protected navTo(ctx: MobCtx, tx: number, ty: number, tz: number, mod: number,
    maxDrop: number, avoidWater: boolean, range = 24, nodes = 300): boolean {
    let nav = this.navigator;
    if (!nav) nav = this.navigator = new Navigator(this.width, this.height);
    const sp = nav.spec;
    sp.maxDrop = maxDrop; sp.waterPenalty = avoidWater ? -1 : 8;
    sp.swim = this.swimmer; sp.lava = this.lavaWalker; sp.climb = this.climbs; sp.doors = this.doorMode(ctx);
    navHost = this.host;
    const st = this.steer;
    if (!nav.update(this.w, this.host.setBlock ? NAV_ENV : null, ctx.tick, this.id,
      this.x, this.y, this.z, tx, ty, tz, range, nodes, st)) return false;
    if (st.dx * st.dx + st.dz * st.dz > 1e-8) this.setMove(st.dx, st.dz, mod);
    if (st.jump) this.wantJump = true;
    return true;
  }

  /** Is rain falling on the mob's cell? */
  protected rainingHere(ctx: MobCtx): boolean {
    return ctx.host.isRainingAt?.(Math.floor(this.x), Math.floor(this.eyeY), Math.floor(this.z)) ?? false;
  }

  /** navTo, or the old direct steering (with the safe-ahead check) when there is no route. */
  protected steerTo(ctx: MobCtx, tx: number, ty: number, tz: number, mod: number, maxDrop: number, avoidWater = false): void {
    if (this.navTo(ctx, tx, ty, tz, mod, maxDrop, avoidWater)) return;
    const dx = tx - this.x, dz = tz - this.z;
    if (this.safeAhead(dx, dz, maxDrop, avoidWater)) this.setMove(dx, dz, mod);
  }

  /** Could the mob stand at feet cell (x, y, z)? Solid floor, body space free, no hazards. */
  private standable(x: number, y: number, z: number, h: number): boolean {
    const w = this.w;
    const fl = w.getBlock(x, y - 1, z), fk = KIND[fl];
    if (this.lavaWalker && fk === K_LAVA && KIND[w.getBlock(x, y, z)] === K_OPEN) return true;
    if (TOP[fl] < 0.4 || TOP[fl] > 1.001 || (fk !== K_OPEN && fk !== K_BLOCKED) || HOT_FLOOR[fl] === 1) return false;
    for (let k = 0; k < h; k++) {
      const c = KIND[w.getBlock(x, y + k, z)];
      if (c !== K_OPEN && c !== K_DOOR_OPEN) return false;
    }
    return true;
  }

  /**
   * Pick a spot to walk to (vanilla RandomPos): mode 0 the best `walkValue` of ten
   * random spots within 10 x 7, 1 a spot 5-10 blocks away on the far side of
   * (fx, fz), 2 an out-of-the-sun spot (sky not open). Result in tgtX/Y/Z.
   */
  private pickTarget(ctx: MobCtx, mode: 0 | 1 | 2, fx: number, fz: number): boolean {
    const rnd = ctx.host.rnd, w = this.w;
    const h = Math.max(1, Math.ceil(this.height - 1e-4));
    const by = Math.floor(this.y + 0.01);
    const vr = mode === 2 ? 3 : 7;
    let best = -Infinity, found = false;
    for (let t = 0; t < 10; t++) {
      let cx: number, cz: number;
      if (mode === 1) {
        // the half-plane away from the threat
        const a = rnd() * Math.PI * 2, r = 5 + rnd() * 5;
        let dx = Math.cos(a) * r, dz = Math.sin(a) * r;
        if (dx * (this.x - fx) + dz * (this.z - fz) < 0) { dx = -dx; dz = -dz; }
        cx = Math.floor(this.x + dx); cz = Math.floor(this.z + dz);
      } else {
        cx = Math.floor(this.x + (rnd() * 2 - 1) * 10); cz = Math.floor(this.z + (rnd() * 2 - 1) * 10);
      }
      if (mode === 0 && this.leashHome) {
        const lh = this.leashHome;
        if ((cx + 0.5 - lh.x) ** 2 + (cz + 0.5 - lh.z) ** 2 > lh.r * lh.r) continue;
      }
      let gy = NO_Y;
      for (let y = by + vr; y >= by - vr; y--) if (this.standable(cx, y, cz, h)) { gy = y; break; }
      if (gy === NO_Y) continue;
      let score: number;
      if (mode === 1) score = (cx + 0.5 - fx) ** 2 + (cz + 0.5 - fz) ** 2;
      else if (mode === 2) {
        if (w.getSky(cx, gy + h - 1, cz) >= 15) continue;
        score = -((cx + 0.5 - this.x) ** 2 + (cz + 0.5 - this.z) ** 2);
      } else score = this.walkValue(cx, gy, cz) + rnd() * 0.01;
      if (score > best) { best = score; found = true; this.tgtX = cx + 0.5; this.tgtY = gy; this.tgtZ = cz + 0.5; }
    }
    return found;
  }

  /** Run to a spot away from (fromX, fromZ) (panic / flee). False: nowhere to go. */
  protected navAway(ctx: MobCtx, fromX: number, fromZ: number, mod: number, maxDrop: number, avoidWater: boolean): boolean {
    const reached = (this.fleeX - this.x) ** 2 + (this.fleeZ - this.z) ** 2 < 1;
    if (this.fleeY === NO_Y || reached || ctx.tick >= this.fleeUntil) {
      if (!this.pickTarget(ctx, 1, fromX, fromZ)) { this.fleeY = NO_Y; return false; }
      this.fleeX = this.tgtX; this.fleeY = this.tgtY; this.fleeZ = this.tgtZ;
      this.fleeUntil = ctx.tick + 40;
    }
    if (this.navTo(ctx, this.fleeX, this.fleeY, this.fleeZ, mod, maxDrop, avoidWater)) return true;
    this.fleeY = NO_Y;
    return false;
  }

  /**
   * Vanilla FleeSunGoal: in open sunlight by day, walk to nearby shade. True while
   * heading for cover (the caller skips its own idling).
   */
  protected seekShade(ctx: MobCtx, mod: number, maxDrop: number): boolean {
    if (ctx.host.sunLevel() <= 0.5 || this.inWater || this.rainingHere(ctx)) { this.shadeY = NO_Y; return false; }
    if (this.w.getSky(Math.floor(this.x), Math.floor(this.eyeY), Math.floor(this.z)) < 15) { this.shadeY = NO_Y; return false; }
    if (this.shadeY === NO_Y || ctx.tick >= this.shadeUntil) {
      if (!this.phase(ctx, 5) || !this.pickTarget(ctx, 2, 0, 0)) return false;
      this.shadeX = this.tgtX; this.shadeY = this.tgtY; this.shadeZ = this.tgtZ;
      this.shadeUntil = ctx.tick + 100;
    }
    if (this.navTo(ctx, this.shadeX, this.shadeY, this.shadeZ, mod, maxDrop, false, 16, 200)) return true;
    this.shadeY = NO_Y;
    return false;
  }

  /** Vanilla RandomStrollGoal: idle a while, then walk to a random nearby spot. */
  protected stroll(ctx: MobCtx, mod: number, maxDrop: number, avoidWater: boolean): void {
    const rnd = ctx.host.rnd;
    if (this.strollMode === 0) {
      if (--this.idleTicks > 0) return;
      this.idleTicks = 40 + Math.floor(rnd() * 120);
      if (this.pickTarget(ctx, 0, 0, 0)) {
        this.strollMode = 1; this.strollTicks = 200;
        this.strollX = this.tgtX; this.strollY = this.tgtY; this.strollZ = this.tgtZ;
      } else {
        // nowhere solid to go (lava striders, ...): wander in a straight line
        const a = rnd() * Math.PI * 2;
        this.strollDX = Math.cos(a); this.strollDZ = Math.sin(a);
        this.strollMode = 2; this.strollTicks = 30 + Math.floor(rnd() * 70);
      }
      return;
    }
    if (--this.strollTicks <= 0) { this.strollMode = 0; return; }
    if (this.strollMode === 2) {
      if (!this.safeAhead(this.strollDX, this.strollDZ, maxDrop, avoidWater)) {
        this.strollMode = 0;
        this.idleTicks = 10 + Math.floor(rnd() * 30);
        return;
      }
      this.setMove(this.strollDX, this.strollDZ, mod);
      return;
    }
    const dx = this.strollX - this.x, dz = this.strollZ - this.z;
    if (dx * dx + dz * dz < 0.5 || !this.navTo(ctx, this.strollX, this.strollY, this.strollZ, mod, maxDrop, avoidWater, 16, 200)) {
      this.strollMode = 0;
      if (dx * dx + dz * dz >= 0.5) this.idleTicks = 10 + Math.floor(rnd() * 30);
    }
  }

  /** Vanilla LookAtPlayerGoal: now and then watch a player within 6 blocks. */
  protected lookAtNearPlayer(ctx: MobCtx): void {
    const p = ctx.host.player;
    if (!p.alive || p.spectator) { this.lookTicks = 0; return; }
    const dx = p.x - this.x, dz = p.z - this.z;
    const near = dx * dx + dz * dz < 36 && Math.abs(p.y - this.y) < 4;
    if (this.lookTicks > 0) {
      this.lookTicks--;
      if (!near) { this.lookTicks = 0; return; }
      this.lookAt(p.x, p.eyeY, p.z);
    } else if (near && ctx.host.rnd() < 0.02) {
      this.lookTicks = 40 + Math.floor(ctx.host.rnd() * 40);
    }
  }

  /** Close enough to land a melee hit on the player? */
  protected inMeleeReach(ctx: MobCtx): boolean {
    const p = ctx.host.player;
    const dx = p.x - this.x, dz = p.z - this.z;
    const reach = this.width / 2 + p.width / 2 + 0.9;
    if (dx * dx + dz * dz > reach * reach) return false;
    return p.y < this.y + this.height && p.y + p.height > this.y;
  }

  // ----------------------------------------------------------- rotation

  private updateRotation(): void {
    let target: number | null = null;
    if (this.faceSet) target = yawTo(this.faceX - this.x, this.faceZ - this.z);
    else if (this.moveMod > 0) target = yawTo(this.moveDX, this.moveDZ);
    if (target !== null) this.yaw = approachAngle(this.yaw, target, BODY_TURN);

    let wantHead = this.yaw;
    let wantPitch = 0;
    if (this.lookSet) {
      const dx = this.lookX - this.x, dz = this.lookZ - this.z;
      wantHead = yawTo(dx, dz);
      wantPitch = Math.atan2(this.lookY - this.eyeY, Math.hypot(dx, dz));
    }
    this.headYaw = approachAngle(this.headYaw, wantHead, HEAD_TURN);
    this.pitch += (wantPitch - this.pitch) * 0.5;
    const off = wrapAngle(this.headYaw - this.yaw);
    if (Math.abs(off) > MAX_HEAD_TURN) {
      // the body follows once the head can't turn further
      this.yaw = wrapAngle(this.headYaw - Math.sign(off) * MAX_HEAD_TURN);
    }
  }

  // ------------------------------------------------------------ physics

  private readonly shapes: ShapesAt = (x, y, z) => {
    if (y < 0) return FULL_CELL_BOXES;
    if (y >= WORLD_HEIGHT) return NO_BOXES;
    return collisionOf(this.w.getBlock(x, y, z));
  };

  private setBox(): number[] {
    const b = this.box, h = this.width / 2;
    b[0] = this.x - h; b[1] = this.y; b[2] = this.z - h;
    b[3] = this.x + h; b[4] = this.y + this.height; b[5] = this.z + h;
    return b;
  }

  private fluidAt(): void {
    const w = this.w;
    const cx = Math.floor(this.x), cz = Math.floor(this.z);
    const lo = w.getBlock(cx, Math.floor(this.y + 0.1), cz);
    const mid = w.getBlock(cx, Math.floor(this.y + this.height * 0.5), cz);
    this.inWater = IS_WATERY[lo] === 1 || IS_WATERY[mid] === 1;
    this.inLava = IS_LAVA[lo] === 1 || IS_LAVA[mid] === 1;
  }

  private sweep(box: number[], dx: number, dy: number, dz: number, out: number[]): void {
    const my = clipMove(box, 1, dy, EPS, this.shapes);
    box[1] += my; box[4] += my;
    let mx: number, mz: number;
    if (Math.abs(dx) >= Math.abs(dz)) {
      mx = clipMove(box, 0, dx, EPS, this.shapes);
      box[0] += mx; box[3] += mx;
      mz = clipMove(box, 2, dz, EPS, this.shapes);
      box[2] += mz; box[5] += mz;
    } else {
      mz = clipMove(box, 2, dz, EPS, this.shapes);
      box[2] += mz; box[5] += mz;
      mx = clipMove(box, 0, dx, EPS, this.shapes);
      box[0] += mx; box[3] += mx;
    }
    out[0] = mx; out[1] = my; out[2] = mz;
  }

  private readonly mv = [0, 0, 0];

  /** Entity.move with step-up. Updates position, collision flags, onGround. */
  private moveBy(dx: number, dy: number, dz: number): void {
    const wasGround = this.onGround;
    const start = this.setBox();
    const startY = start[1]; // setBox() reuses one buffer: the onGround probe below overwrites `start`
    const box = start.slice();
    const m = this.mv;
    this.sweep(box, dx, dy, dz, m);
    let mx = m[0], my = m[1], mz = m[2];
    const blockedH = mx !== dx || mz !== dz;
    const blockedY = my !== dy;
    if (blockedH && (wasGround || (dy < 0 && my !== dy))) {
      const b2 = start.slice();
      const up = clipMove(b2, 1, this.stepHeight, EPS, this.shapes);
      b2[1] += up; b2[4] += up;
      const m2 = [0, 0, 0];
      this.sweep(b2, dx, 0, dz, m2);
      const down = clipMove(b2, 1, -up, EPS, this.shapes);
      b2[1] += down; b2[4] += down;
      if (m2[0] * m2[0] + m2[2] * m2[2] > mx * mx + mz * mz + 1e-9) {
        box[0] = b2[0]; box[1] = b2[1]; box[2] = b2[2]; box[3] = b2[3]; box[4] = b2[4]; box[5] = b2[5];
        mx = m2[0]; mz = m2[2];
        my = box[1] - start[1];
      }
    }
    this.x = (box[0] + box[3]) / 2;
    this.y = box[1];
    this.z = (box[2] + box[5]) / 2;
    this.collidedH = mx !== dx || mz !== dz;
    if (mx !== dx) this.vx = 0;
    if (mz !== dz) this.vz = 0;
    if (blockedY) this.vy = 0;
    // ground contact: something within a hair below the feet
    this.onGround = this.vy <= 0 && clipMove(this.setBox(), 1, -0.01, EPS, this.shapes) > -0.0099;
    // vertical displacement for fall tracking
    this.lastDy = this.y - startY;
  }
  private lastDy = 0;

  private travel(): void {
    this.fluidAt();
    const swimming = this.inWater || this.inLava;
    if (this.wantJump) {
      if (swimming) this.vy += 0.04;
      else if (this.onGround) this.vy = JUMP_VELOCITY;
    } else if (swimming && this.floats()) {
      // vanilla FloatGoal: bob at the surface
      if (this.host.rnd() < 0.8) this.vy += 0.04;
    }
    if (this.moveMod > 0 && this.collidedH && this.onGround && !this.dying) this.vy = JUMP_VELOCITY;

    // acceleration
    let drag: number;
    const m = this.moveMod * this.speed * (this.effects ? speedMult(this.effects) : 1);
    if (swimming) {
      drag = this.inLava ? 0.5 : 0.8;
      if (m > 0) { this.vx += this.moveDX * 0.02 * (1 + this.moveMod); this.vz += this.moveDZ * 0.02 * (1 + this.moveMod); }
    } else if (this.onGround) {
      drag = GROUND_DRAG;
      if (m > 0) { this.vx += this.moveDX * m * (1 - GROUND_DRAG); this.vz += this.moveDZ * m * (1 - GROUND_DRAG); }
    } else {
      drag = 0.91;
      if (m > 0) { this.vx += this.moveDX * 0.02; this.vz += this.moveDZ * 0.02; }
    }

    if (this.climbs && this.collidedH && this.moveMod > 0 && !this.dying) this.vy = 0.2;
    this.moveBy(this.vx, this.vy, this.vz);

    // fall tracking (vanilla: damage on landing = fall distance - 3)
    if (swimming) this.fallDistance = 0;
    else if (this.lastDy < 0) this.fallDistance -= this.lastDy;
    if (this.onGround) {
      if (this.fallDistance > 3 && !this.noFallDamage) this.hurt(Math.ceil(this.fallDistance - 3));
      this.fallDistance = 0;
    }

    // gravity + drag
    if (swimming) this.vy = (this.vy - 0.02) * (this.inLava ? 0.5 : 0.8);
    else {
      this.vy -= GRAVITY;
      if (this.slowFall && this.vy < 0 && !this.onGround) this.vy *= 0.6;
      this.vy *= 0.98;
    }
    if (this.climbs && this.collidedH && this.moveMod > 0 && !this.dying) this.vy = Math.max(this.vy, 0.1);
    this.vx *= drag;
    this.vz *= drag;
    if (Math.abs(this.vx) < 0.003) this.vx = 0;
    if (Math.abs(this.vz) < 0.003) this.vz = 0;
  }

  protected floats(): boolean { return true; }

  private postTravel(ctx: MobCtx): void {
    // lava: 4 damage per hit (invulnerability gates it to every 10 ticks) + fire
    if (this.inLava) {
      this.fireTicks = Math.max(this.fireTicks, 300);
      this.hurt(4);
    }
    if (this.inWater && this.fireTicks > 0) this.fireTicks = 0;
    if (this.fireTicks > 0) {
      if (this.fireTicks % 20 === 0) this.hurt(1);
      this.fireTicks--;
    }
    if (this.y < -20) { this.removed = true; return; }
    void ctx;
  }

  private updateLimbs(): void {
    const dx = this.x - this.px, dz = this.z - this.pz;
    const f = Math.min(Math.sqrt(dx * dx + dz * dz) * 4, 1);
    this.limbSwingAmount += (f - this.limbSwingAmount) * 0.4;
    this.limbSwing += this.limbSwingAmount;
  }

  /** Burn-in-daylight check shared by zombie and skeleton. */
  protected sunBurn(ctx: MobCtx): void {
    const sun = ctx.host.sunLevel();
    if (sun <= 0.5 || this.inWater || this.fireTicks > 0 || this.rainingHere(ctx)) return;
    if ((this.armorPieces[0] ?? 0) > 0) return; // vanilla: a helmet shields undead from the sun
    const cx = Math.floor(this.x), cz = Math.floor(this.z);
    const sky = this.w.getSky(cx, Math.floor(this.eyeY), cz);
    if (sky < 15) return;
    if (ctx.host.rnd() * 30 < (sun - 0.4) * 2) this.fireTicks = 160;
  }

  /** Light at the mob for the renderer, 0..1. */
  lightLevel(host: EntityHost): number {
    // raw sky + block light, like the terrain shader: the renderer's ambient
    // uniform already carries the time of day, so scaling by daylight here
    // too left mobs near-black at night
    const bx = Math.floor(this.x), by = Math.floor(this.y + this.height * 0.5), bz = Math.floor(this.z);
    return Math.max(host.world.getSky(bx, by, bz), host.world.getBlockLight(bx, by, bz)) / 15;
  }

  /** Push-out from another mob overlapping this one (vanilla: 0.05 impulse). */
  pushFrom(o: Mob): void {
    const dx = o.x - this.x, dz = o.z - this.z;
    const r = (this.width + o.width) / 2;
    if (Math.abs(dx) >= r || Math.abs(dz) >= r) return;
    if (this.y >= o.y + o.height || o.y >= this.y + this.height) return;
    let d = Math.max(Math.abs(dx), Math.abs(dz));
    if (d < 0.01) return;
    d = Math.sqrt(d);
    const nx = dx / d, nz = dz / d;
    const inv = Math.min(1, 1 / d);
    this.vx -= nx * inv * 0.05; this.vz -= nz * inv * 0.05;
    o.vx += nx * inv * 0.05; o.vz += nz * inv * 0.05;
  }

  syncView(host: EntityHost): void {
    const v = this.view;
    v.x = this.x; v.y = this.y; v.z = this.z;
    v.px = this.px; v.py = this.py; v.pz = this.pz;
    v.yaw = this.yaw; v.headYaw = this.headYaw; v.pitch = this.pitch;
    v.limbSwing = this.limbSwing; v.limbSwingAmount = this.limbSwingAmount;
    v.hurtTime = this.hurtTime; v.deathTime = this.deathTime;
    v.swell = this.swell;
    v.attackAnim = this.swingTicks > 0 ? this.swingTicks / ATTACK_SWING_TICKS : 0;
    v.onFire = this.fireTicks > 0;
    if (this.armorPieces.length) v.armor = this.armorPieces;
    v.heldItem = this.heldItem; v.sheared = this.sheared; v.color = this.color;
    v.aggressive = this.aggressive;
    v.saddled = this.saddled; v.ridden = this.rideCtl !== null;
    v.name = this.customName || undefined;
    v.light = this.lightLevel(host);
  }
}
